import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { blogPosts, AUTHOR, blogImages } from "@/data/blogPosts";
import { SITE_URL } from "@/data/servicePages";

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPost({ slug }) {
  const post = blogPosts[slug];
  const url = `${SITE_URL}/insights/${slug}`;

  const jsonLd = useMemo(() => {
    if (!post) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_URL}/insights` },
          { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.metaDescription,
        url,
        datePublished: post.published,
        dateModified: post.updated,
        author: { "@type": "Organization", name: AUTHOR.name, description: AUTHOR.description },
        publisher: { "@type": "Organization", name: "Aurelius Family Office", url: `${SITE_URL}/` },
        mainEntityOfPage: url,
        articleSection: post.category,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, [post, slug, url]);

  useSEO({
    title: post?.metaTitle,
    description: post?.metaDescription,
    keywords: post?.keywords,
    canonical: url,
    ogTitle: post?.title,
    jsonLd,
  });

  useEffect(() => window.scrollTo(0, 0), [slug]);
  if (!post) return null;

  return (
    <div className="bg-paper">
      <Navbar />

      {/* Header */}
      <section className="relative bg-brand overflow-hidden">
        {blogImages[slug] && (
          <>
            <img src={blogImages[slug]} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-brand/85" />
          </>
        )}
        <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 lg:px-12 pt-36 pb-16">
          <Link to="/insights" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Insights
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-200 mb-4">{post.category}</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] tracking-tight max-w-4xl">
            {post.title}
          </h1>
          <p className="mt-6 text-sm text-white/70">
            By {AUTHOR.name} · Published {fmtDate(post.published)} · Updated {fmtDate(post.updated)} · {post.readTime} min read
          </p>
        </div>
      </section>

      {/* TL;DR answer box (AEO) */}
      <section className="bg-white pt-16 md:pt-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="border-s-2 border-brand-600 bg-slate-50 p-8 md:p-10 max-w-4xl">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3">The short answer</p>
              <p className="font-outfit text-lg md:text-xl text-slate-900 leading-relaxed">{post.answer}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Article body */}
      <article className="bg-white py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl space-y-14">
            {post.sections.map((s, i) => (
              <Reveal key={i}>
                <section>
                  <h2 className="font-outfit text-2xl md:text-3xl font-medium tracking-tight text-slate-950 mb-6">{s.h2}</h2>
                  <div className="space-y-5">
                    {s.body.map((p, j) => (
                      <p key={j} className="text-base text-slate-600 leading-relaxed">{p}</p>
                    ))}
                  </div>
                  {s.list && (
                    <ul className="mt-6 space-y-3">
                      {s.list.map((li, j) => (
                        <li key={j} className="flex gap-3 text-base text-slate-600 leading-relaxed">
                          <span className="mt-2.5 w-1.5 h-1.5 bg-brand-600 shrink-0" />
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.table && (
                    <div className="mt-8 overflow-x-auto">
                      <table className="w-full border border-slate-200 text-sm">
                        <thead>
                          <tr className="bg-slate-50">
                            {s.table.head.map((h, j) => (
                              <th key={j} className="p-4 text-start font-outfit font-medium text-slate-950 border-b border-slate-200">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.table.rows.map((r, j) => (
                            <tr key={j} className="border-b border-slate-200 last:border-0">
                              {r.map((c, k) => (
                                <td key={k} className={`p-4 ${k === 0 ? "text-slate-950 font-medium" : "text-slate-600"}`}>{c}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </Reveal>
            ))}
          </div>
        </div>
      </article>

      {/* FAQ */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">FAQ</p>
            <h2 className="font-outfit text-3xl font-medium tracking-tight text-slate-950">Common questions</h2>
          </Reveal>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {post.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-slate-200">
                  <AccordionTrigger className="text-start font-outfit text-base md:text-lg font-medium text-slate-950 hover:text-brand-700 hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-slate-600 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Sources + related */}
      <section className="bg-white py-16 md:py-20 border-t border-slate-200">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-6">Sources & further reading</p>
            <ul className="space-y-3">
              {post.sources.map((src, i) => (
                <li key={i}>
                  <a href={src.url} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-slate-600 hover:text-brand-700 underline underline-offset-4 decoration-slate-300">
                    {src.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-6">Related</p>
            <ul className="space-y-3">
              {post.related.map((r, i) => (
                <li key={i}>
                  <Link to={r.to} className="group inline-flex items-center gap-2 text-sm text-slate-950 font-medium hover:text-brand-700 transition-colors">
                    {r.label}
                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-brand-700 transition-colors" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light text-white">Talk this through with a practitioner.</h2>
            <p className="mt-3 text-slate-400">Confidential, no-obligation conversation with a senior partner.</p>
          </Reveal>
          <Link to="/#consultation" className="bg-white hover:bg-brand-50 transition-colors text-brand px-8 py-4 text-sm tracking-widest uppercase rounded-none shrink-0">
            Request Consultation
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
